/**
 * HTTP-клієнт інтерфейсу. Помилки API — коди з параметрами, перекладає клієнт
 * (stack.md, «i18n з першого дня»); тут — мінімум для spike: статус і код для підтримки.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    detail?: string,
  ) {
    super(detail ? `${code} (HTTP ${status}): ${detail}` : `${code} (HTTP ${status})`);
    this.name = 'ApiError';
  }
}

declare global {
  interface Window {
    /** Ранні запити першого екрана з public/boot.js: адреса → відповідь (або null при помилці). */
    __clEarly?: Record<string, Promise<Response | null> | undefined>;
  }
}

/** Забрати ранню відповідь з boot.js: кожну — один раз (тіло відповіді читається лише раз). */
export function takeEarly(url: string): Promise<Response | null> | undefined {
  const early = typeof window === 'undefined' ? undefined : window.__clEarly;
  const pending = early?.[url];
  if (early && pending) delete early[url];
  return pending;
}

export async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = (await takeEarly(url)) ?? (await fetch(url, { signal: signal ?? null }));
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NET-000');
  }
  // SPA-fallback edge віддає index.html на невідомий шлях — це теж «не знайдено».
  const type = res.headers.get('content-type') ?? '';
  if (res.status === 404 || (res.ok && !type.includes('json'))) throw new ApiError(404, 'REC-404');
  if (!res.ok) throw new ApiError(res.status, `API-${res.status}`);
  return (await res.json()) as T;
}
